import { useEffect, useRef } from "react";
import { micLevel } from "@/lib/audioLevel";

// One living form, never a face. It does not switch between drawings — every parameter
// eases toward the current state, so idle breathing becomes listening becomes thinking
// becomes speaking as a single continuous morph.
//   idle       slow breath, almost still
//   listening  swells toward your voice, a lobe leaning in
//   reflecting draws inward, creases, turns over on itself
//   speaking   unfurls, brightens, pushes rings outward
const TARGET = {
  idle: { size: 0.6, wobble: 0.028, lean: 0.0, fold: 0.0, heat: 0.2, rings: 0.0, speed: 0.5, edge: 0.62 },
  listening: { size: 0.6, wobble: 0.05, lean: 1.0, fold: 0.0, heat: 0.4, rings: 0.45, speed: 0.95, edge: 0.85 },
  reflecting: { size: 0.47, wobble: 0.075, lean: 0.0, fold: 1.0, heat: 0.3, rings: 0.0, speed: 1.75, edge: 0.6 },
  speaking: { size: 0.66, wobble: 0.1, lean: 0.0, fold: 0.0, heat: 0.78, rings: 1.0, speed: 2.1, edge: 1.0 },
};

const HUE = {
  idle: null, // the Lab's own accent
  listening: "#E08D46",
  reflecting: "#D4A359",
  speaking: "#E66A4E",
};

const rgb = (h) => {
  const n = (h || "#C87D55").replace("#", "");
  const v = n.length === 3 ? n.split("").map((c) => c + c).join("") : n;
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
};
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const ca = ([r, g, b], a) => `rgba(${r},${g},${b},${a})`;

export const Presence = ({ state = "idle", accent = "#C87D55", height = 260, className = "" }) => {
  const ref = useRef(null);
  const live = useRef(state);
  live.current = state;

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf;
    let t = 0;
    let phase = 0; // advances with `speed`, so the form never jumps when the state changes

    const w = () => canvas.clientWidth;
    const resize = () => {
      canvas.width = w() * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const cur = { ...TARGET.idle };
    const base = rgb(accent);
    const tintCur = [...base];
    const rings = [];
    let lvl = 0;
    let lastRing = 0;

    // the contour: a closed loop of layered sine noise, sampled once per frame
    const STEP = Math.PI / 72;
    const contour = (cx, cy, R, scale, tint, alpha, lineWidth, fill) => {
      ctx.beginPath();
      for (let a = 0; a <= Math.PI * 2 + 0.001; a += STEP) {
        const noise =
          Math.sin(a * 3 + phase * 1.1) * 0.5 +
          Math.sin(a * 5 - phase * 0.78) * 0.3 +
          Math.sin(a * 7 + phase * 1.6) * 0.2;
        const crease = cur.fold * Math.sin(a * 6 - phase * 0.9) * 0.085;
        const lean = cur.lean * lvl * Math.cos(a - Math.PI / 2) * 0.2;
        const rr = R * scale * (1 + noise * (cur.wobble + lvl * 0.11) + crease + lean);
        const x = cx + Math.cos(a) * rr;
        const y = cy + Math.sin(a) * rr * 0.92;
        a === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();
      if (fill) {
        ctx.fillStyle = fill;
        ctx.fill();
      }
      if (lineWidth) {
        ctx.lineWidth = lineWidth;
        ctx.strokeStyle = ca(tint, alpha);
        ctx.stroke();
      }
    };

    const draw = () => {
      const s = live.current;
      const tgt = TARGET[s] || TARGET.idle;
      Object.keys(tgt).forEach((k) => { cur[k] += (tgt[k] - cur[k]) * 0.035; });

      const breath = 1 + Math.sin(t * 1.25) * (0.03 + (1 - cur.heat) * 0.025);
      const raw = s === "listening" ? Math.min(1, micLevel() * 5.2) : 0;
      const voice = s === "speaking"
        ? 0.42 + 0.58 * Math.abs(Math.sin(t * 2.1) * 0.6 + Math.sin(t * 5.3) * 0.26 + Math.sin(t * 9.7) * 0.14)
        : 0;
      lvl += ((s === "listening" ? raw : voice) - lvl) * 0.14;

      const W = w();
      const H = height;
      const cx = W / 2;
      const cy = H / 2;
      const R = Math.min(W * 0.44, H * 0.46) * cur.size * breath * (1 + lvl * 0.07);
      const want = rgb(HUE[s] || accent);
      for (let i = 0; i < 3; i++) tintCur[i] += (mix(base, want, 0.7)[i] - tintCur[i]) * 0.04;
      const tint = tintCur.map(Math.round);

      t += 0.0135;
      phase += 0.0135 * cur.speed;
      ctx.clearRect(0, 0, W, H);

      // the hearth behind it
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.min(W, H) * 0.55);
      glow.addColorStop(0, ca(tint, 0.1 + (cur.heat + lvl * 0.25) * 0.17));
      glow.addColorStop(0.55, ca(tint, 0.03 + cur.heat * 0.05));
      glow.addColorStop(1, ca(tint, 0));
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, W, H);

      // rings leaving the body: your voice arriving, or Griot's going out
      if (cur.rings > 0.12 && lvl > 0.12 && t - lastRing > 0.3) {
        rings.push({ r: R, a: Math.min(0.42, 0.16 + lvl * 0.4) });
        lastRing = t;
      }
      for (let i = rings.length - 1; i >= 0; i--) {
        const p = rings[i];
        p.r += 1.8 + lvl * 1.4;
        p.a *= 0.968;
        if (p.a < 0.012 || p.r > Math.max(W, H)) { rings.splice(i, 1); continue; }
        ctx.beginPath();
        ctx.ellipse(cx, cy, p.r, p.r * 0.92, 0, 0, Math.PI * 2);
        ctx.lineWidth = 1;
        ctx.strokeStyle = ca(tint, p.a * cur.rings);
        ctx.stroke();
      }

      // the body: a soft gradient skin, then two inner echoes, then the lit edge
      const skin = ctx.createRadialGradient(cx, cy - R * 0.18, 0, cx, cy, R * 1.15);
      skin.addColorStop(0, ca(mix(tint, [255, 238, 214], 0.35), 0.12 + cur.heat * 0.2));
      skin.addColorStop(0.65, ca(tint, 0.06 + cur.heat * 0.09));
      skin.addColorStop(1, ca(tint, 0));
      contour(cx, cy, R, 1, tint, 0, 0, skin);
      contour(cx, cy, R, 0.72, tint, 0.1 + cur.fold * 0.16, 0.8);
      contour(cx, cy, R, 0.45, tint, 0.08 + cur.fold * 0.2, 0.6);

      ctx.shadowBlur = 16 + cur.heat * 26;
      ctx.shadowColor = ca(tint, 0.35 + cur.heat * 0.3);
      contour(cx, cy, R, 1, mix(tint, [255, 240, 220], 0.25), 0.3 + cur.edge * 0.45, 1.5);
      ctx.shadowBlur = 0;

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [height, accent]);

  return (
    <canvas
      ref={ref}
      data-testid="waveform-canvas"
      data-presence-state={state}
      data-state={state}
      className={`block w-full ${className}`}
      style={{ height }}
    />
  );
};
